import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { showSaveSuccess, showSaveError } from '@/lib/swal'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAuthStore } from '@/store/auth.store'
import { expenseClaimService } from '@/services/expenseClaimService'
import type {
  ExpenseClaimLineVm,
  ExpenseClaimLookupItem,
  ExpenseClaimSavePayload,
  ExpenseClaimLineSavePayload,
} from '@/api/types'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

const CLAIM_TYPES = [
  { value: 'Personal', label: 'Personal' },
  { value: 'Business', label: 'Business' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Other', label: 'Other' },
]

function emptyLine(taxRates: ExpenseClaimLookupItem[]): ExpenseClaimLineVm {
  const defaultTax = taxRates[0]
  const taxPercent = defaultTax?.taxPercent ?? defaultTax?.taxRate ?? 0
  return {
    invoiceDate: '',
    expenseTypeId: 0,
    description: '',
    taxableAmount: 0,
    taxRateId: defaultTax?.id ?? 0,
    taxPercent,
    taxAmount: 0,
    amount: 0,
  }
}

function calcLine(line: ExpenseClaimLineVm): { taxAmount: number; amount: number } {
  const taxable = Number(line.taxableAmount) || 0
  const pct = Number(line.taxPercent) || 0
  const taxAmount = Math.round(taxable * (pct / 100) * 100) / 100
  const amount = taxable + taxAmount
  return { taxAmount, amount }
}

export const ExpenseClaimDetail: React.FC = () => {
  const { i18n } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [departments, setDepartments] = useState<ExpenseClaimLookupItem[]>([])
  const [employees, setEmployees] = useState<ExpenseClaimLookupItem[]>([])
  const [expenseTypes, setExpenseTypes] = useState<ExpenseClaimLookupItem[]>([])
  const [taxRates, setTaxRates] = useState<ExpenseClaimLookupItem[]>([])

  const [departmentId, setDepartmentId] = useState<number>(0)
  const [employeeId, setEmployeeId] = useState<number>(0)
  const [claimType, setClaimType] = useState<string>('Personal')
  const [expenseDate, setExpenseDate] = useState<string>(todayIso())

  const [lines, setLines] = useState<ExpenseClaimLineVm[]>([])
  const [lineFiles, setLineFiles] = useState<Record<number, File>>({})
  const [datePopoverIndex, setDatePopoverIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [deptList, empList, expList, taxList] = await Promise.all([
          expenseClaimService.getDepartments(branchId),
          expenseClaimService.getEmployees(branchId),
          expenseClaimService.getExpenseTypes(branchId),
          expenseClaimService.getTaxRates(branchId),
        ])
        setDepartments(deptList || [])
        setEmployees(empList || [])
        setExpenseTypes(expList || [])
        setTaxRates(taxList || [])
        if (lines.length === 0) {
          setLines(Array.from({ length: 3 }, () => emptyLine(taxList || [])))
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load form lookups.')
      }
    }
    loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || isNew || !editId) return
    const load = async () => {
      try {
        setLoading(true)
        const [vm, lineList] = await Promise.all([
          expenseClaimService.getById(editId, branchId),
          expenseClaimService.getLinesById(editId, branchId),
        ])
        if (!vm) {
          navigate('/self-services/expense-claims')
          return
        }
        setDepartmentId(vm.departmentId ?? 0)
        setEmployeeId(vm.employeeId ?? 0)
        setClaimType(vm.claimType ?? 'Personal')
        setExpenseDate(vm.expenseDate ? String(vm.expenseDate).slice(0, 10) : todayIso())
        const loadedLines = (lineList && lineList.length > 0) ? lineList : [emptyLine(taxRates)]
        setLines(loadedLines)
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load expense claim.')
        navigate('/self-services/expense-claims')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, isNew, editId, navigate])

  const employeeOptions = useMemo((): SearchableSelectOption[] => {
    const list = departmentId
      ? employees.filter((e) => (e.departmentId ?? 0) === departmentId)
      : employees
    return [
      { value: '0', label: 'Select employee' },
      ...list.filter((e) => e.id).map((e) => ({
        value: String(e.id),
        label: e.name ?? e.nameEn ?? e.lookupNameEn ?? `Employee ${e.id}`,
      })),
    ]
  }, [employees, departmentId])

  const departmentOptions = useMemo((): SearchableSelectOption[] => {
    return [
      { value: '0', label: 'Select department' },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.name ?? d.nameEn ?? d.lookupNameEn ?? `Department ${d.id}`,
      })),
    ]
  }, [departments])

  const expenseTypeOptions = useMemo((): SearchableSelectOption[] => {
    const useAr = i18n.language?.toLowerCase().startsWith('ar')
    const labelFor = (e: ExpenseClaimLookupItem) => {
      if (useAr) {
        return (
          e.lookupNameAr ||
          e.nameAr ||
          e.lookupNameEn ||
          e.nameEn ||
          e.name ||
          `Type ${e.id}`
        )
      }
      return (
        e.lookupNameEn ||
        e.nameEn ||
        e.lookupNameAr ||
        e.nameAr ||
        e.name ||
        `Type ${e.id}`
      )
    }
    return [
      { value: '0', label: 'Select' },
      ...expenseTypes.filter((e) => e.id).map((e) => ({
        value: String(e.id),
        label: labelFor(e),
      })),
    ]
  }, [expenseTypes, i18n.language])

  const taxRateOptions = useMemo((): SearchableSelectOption[] => {
    return [
      { value: '0', label: 'Select' },
      ...taxRates.filter((t) => t.id).map((t) => {
        const label = (t.name ?? t.nameEn ?? t.lookupNameEn) || (String(t.taxPercent ?? t.taxRate ?? 0) + '%')
        return { value: String(t.id), label }
      }),
    ]
  }, [taxRates])

  const updateLine = (index: number, field: keyof ExpenseClaimLineVm, value: any) => {
    setLines((prev) => {
      const next = [...prev]
      const line = { ...next[index], [field]: value }
      if (field === 'taxableAmount' || field === 'taxPercent' || field === 'taxRateId') {
        if (field === 'taxRateId' && value) {
          const t = taxRates.find((r) => r.id === Number(value))
          if (t) line.taxPercent = t.taxPercent ?? t.taxRate ?? 0
        }
        const { taxAmount, amount } = calcLine(line)
        line.taxAmount = taxAmount
        line.amount = amount
      }
      next[index] = line
      return next
    })
  }

  const addRows = (count: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: count }, () => emptyLine(taxRates))])
  }

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
    setLineFiles((prev) => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const totals = useMemo(() => {
    const subTotal = lines.reduce((s, l) => s + (Number(l.taxableAmount) || 0), 0)
    const totalVat = lines.reduce((s, l) => s + (Number(l.taxAmount) || 0), 0)
    const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0)
    return { subTotal, totalVat, total }
  }, [lines])

  const claimTypeToNumber: Record<string, number> = {
    Personal: 1,
    Business: 2,
    Travel: 3,
    Other: 4,
  }

  const toIsoDate = (dateStr: string) => {
    if (!dateStr || dateStr.length < 10) return new Date().toISOString()
    return new Date(dateStr + 'T00:00:00').toISOString()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return
    if (!departmentId) {
      await showSaveError({ message: 'Department is required.' })
      return
    }
    if (!employeeId) {
      await showSaveError({ message: 'Employee is required.' })
      return
    }
    if (!expenseDate) {
      await showSaveError({ message: 'Expense Date is required.' })
      return
    }

    const nowIso = new Date().toISOString()
    const spendDateIso = toIsoDate(expenseDate)
    const claimDateIso = toIsoDate(expenseDate)

    const isRowFilled = (l: ExpenseClaimLineVm) => {
      const hasDesc = (l.description ?? '').trim().length > 0
      const hasAmount = Number(l.taxableAmount) > 0
      const hasType = Number(l.expenseTypeId) > 0
      const hasDate = !!(l.invoiceDate && String(l.invoiceDate).trim().length >= 10)
      return hasDesc || hasAmount || hasType || hasDate
    }
    const filledLineEntries = lines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => isRowFilled(line))
    const filledLines = filledLineEntries.map(({ line }) => line)

    const expenseClaimD: ExpenseClaimLineSavePayload[] = filledLineEntries.map(({ line: l, index }, payloadLineIndex) => ({
      lineId: l.id ?? 0,
      invoiceDate: l.invoiceDate ? toIsoDate(String(l.invoiceDate).slice(0, 10)) : nowIso,
      description: l.description ?? '',
      price: Number(l.taxableAmount) || 0,
      vatPercent: Number(l.taxPercent) || 0,
      vatAmount: Number(l.taxAmount) || 0,
      lineAmount: Number(l.amount) || 0,
      claimId: isNew ? 0 : editId,
      createdDate: nowIso,
      createdBy: 0,
      updatedById: 0,
      updatedDate: nowIso,
      attachment: l.attachmentUrl ?? '',
      // This is a string field in JSON preview; actual file is sent via FormData in the service.
      attachmentFile: lineFiles[index]?.name ?? payloadLineFiles[payloadLineIndex]?.name ?? '',
      isEditable: 0,
      category: Number(l.expenseTypeId) || 0,
      isDeleted: 'false',
      branchId,
    }))

    const payloadAmount = filledLines.reduce((s, l) => s + (Number(l.amount) || 0), 0)

    const payload: ExpenseClaimSavePayload = {
      id: isNew ? 0 : editId,
      empId: employeeId,
      departmentId,
      claimType: claimTypeToNumber[claimType] ?? 0,
      title: filledLines[0]?.description?.trim() ?? 'Expense Claim',
      spendDate: spendDateIso,
      claimDate: claimDateIso,
      amount: payloadAmount,
      branchId,
      details: filledLines.map((l) => l.description).filter(Boolean).join('; ') || 'Expense Claim',
      expenseClaimD,
    }

    const payloadLineFiles: Record<number, File | undefined> = {}
    filledLineEntries.forEach(({ index }, payloadLineIndex) => {
      payloadLineFiles[payloadLineIndex] = lineFiles[index]
    })

    const payloadPreview = {
      ...payload,
      expenseClaimD: payload.expenseClaimD.map((line, idx) => ({
        ...line,
        attachment: payloadLineFiles[idx]?.name ? `[FILE] ${payloadLineFiles[idx]?.name}` : line.attachment,
        attachmentFile: payloadLineFiles[idx]?.name ? `[FILE] ${payloadLineFiles[idx]?.name}` : line.attachmentFile,
      })),
    }

    console.log('Expense Claim Save Payload:', payload)
    console.log('Expense Claim Save Payload (JSON):', JSON.stringify(payload, null, 2))
    console.log('Expense Claim Save Payload Preview (with attachment files):', payloadPreview)
    console.log(
      'Expense Claim Line Files:',
      Object.entries(payloadLineFiles).map(([lineIndex, file]) => ({
        lineIndex: Number(lineIndex),
        fileName: file?.name ?? '',
        fileSize: file?.size ?? 0,
        fileType: file?.type ?? '',
      }))
    )

    try {
      setSaving(true)
      if (isNew) {
        await expenseClaimService.save(payload, payloadLineFiles)
        await showSaveSuccess(false)
      } else {
        await expenseClaimService.update(payload, payloadLineFiles)
        await showSaveSuccess(true)
      }
      navigate('/self-services/expense-claims')
    } catch (err: any) {
      await showSaveError(err?.response?.data?.message ?? err?.message ?? 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Expense Claim Request" description="Expense Claim Detail" />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Expense Claim Request" description="Expense Claim Detail" />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <SearchableSelect
                  label="Department *"
                  options={departmentOptions}
                  value={departmentId ? String(departmentId) : '0'}
                  onValueChange={(v) => {
                    setDepartmentId(Number(v) || 0)
                    setEmployeeId(0)
                  }}
                  placeholder="Select department"
                  searchPlaceholder="Search department..."
                  emptyMessage="No department found"
                />
              </div>
              <div className="space-y-2">
                <SearchableSelect
                  label="Employee *"
                  options={employeeOptions}
                  value={employeeId ? String(employeeId) : '0'}
                  onValueChange={(v) => setEmployeeId(Number(v) || 0)}
                  placeholder="Select employee"
                  searchPlaceholder="Search employee..."
                  emptyMessage="No employee found"
                />
              </div>
              <div className="space-y-2">
                <Label>Claim Type *</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={claimType}
                  onChange={(e) => setClaimType(e.target.value)}
                >
                  {CLAIM_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Expense Date *</Label>
                <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader className="bg-primary/5">
            <div className="flex items-center justify-end">
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addRows(1)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addRows(3)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add 3 Rows
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addRows(5)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add 5 Rows
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto overflow-y-auto max-h-[60vh] rounded-md border border-input">
              <table className="w-full border-collapse table-fixed min-w-[900px]">
                <thead className="sticky top-0 z-10 bg-primary/5">
                  <tr className="border-b-2 border-primary/20">
                    <th className="text-left p-2 font-semibold w-[130px] min-w-[130px]">Invoice Date</th>
                    <th className="text-left p-2 font-semibold w-[140px] min-w-[140px]">Expense Type</th>
                    <th className="text-left p-2 font-semibold min-w-[160px]">Description</th>
                    <th className="text-right p-2 font-semibold w-[120px] min-w-[120px]">Taxable Amount</th>
                    <th className="text-left p-2 pr-5 font-semibold w-[100px] min-w-[100px]">Tax %</th>
                    <th className="text-right p-2 pl-4 font-semibold w-[100px] min-w-[100px]">Tax Amount</th>
                    <th className="text-right p-2 font-semibold w-[100px] min-w-[100px]">Amount</th>
                    <th className="text-left p-2 font-semibold w-[180px] min-w-[180px]">Attachment</th>
                    <th className="w-12 p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={index} className="border-b border-input hover:bg-muted/20">
                      <td className="p-2 align-middle">
                        <Popover open={datePopoverIndex === index} onOpenChange={(open) => setDatePopoverIndex(open ? index : null)}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex h-10 w-full min-w-[120px] items-center rounded-md border border-input bg-background px-3 py-2 text-left text-sm cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <span className={line.invoiceDate ? '' : 'text-muted-foreground'}>
                                {line.invoiceDate ? String(line.invoiceDate).slice(0, 10) : 'mm/dd/yyyy'}
                              </span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-3" align="start">
                            <input
                              type="date"
                              value={line.invoiceDate ? String(line.invoiceDate).slice(0, 10) : ''}
                              onChange={(e) => {
                                updateLine(index, 'invoiceDate', e.target.value)
                                setDatePopoverIndex(null)
                              }}
                              className="flex h-10 w-full min-w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              autoFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </td>
                      <td className="p-2 align-middle">
                        <div className="h-10 min-w-[120px] flex items-center [&_button]:h-10 [&_button]:border [&_button]:border-input [&_button]:rounded-md [&_button]:bg-background">
                          <SearchableSelect
                            options={expenseTypeOptions}
                            value={line.expenseTypeId ? String(line.expenseTypeId) : '0'}
                            onValueChange={(v) => updateLine(index, 'expenseTypeId', Number(v) || 0)}
                            placeholder="Select"
                            searchPlaceholder="Search..."
                            emptyMessage="No type found"
                            className="h-10 w-full min-w-[120px]"
                          />
                        </div>
                      </td>
                      <td className="p-2 align-middle">
                        <Input
                          value={line.description ?? ''}
                          onChange={(e) => updateLine(index, 'description', e.target.value)}
                          placeholder="Description"
                          className="h-10 w-full min-w-[140px] border border-input bg-background rounded-md text-sm"
                        />
                      </td>
                      <td className="p-2 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.taxableAmount ?? ''}
                          onChange={(e) => updateLine(index, 'taxableAmount', Number(e.target.value) || 0)}
                          className="h-10 w-full min-w-[90px] text-right border border-input bg-background rounded-md text-sm"
                        />
                      </td>
                      <td className="p-2 pr-5 align-middle">
                        <div className="h-10 min-w-[90px] flex items-center [&_button]:h-10 [&_button]:border [&_button]:border-input [&_button]:rounded-md [&_button]:bg-background [&_button]:font-semibold [&_button]:text-foreground [&_button]:text-sm">
                          <SearchableSelect
                            options={taxRateOptions}
                            value={line.taxRateId ? String(line.taxRateId) : '0'}
                            onValueChange={(v) => updateLine(index, 'taxRateId', Number(v) || 0)}
                            placeholder="Select %"
                            searchPlaceholder="Search..."
                            emptyMessage="No rate"
                            className="h-10 w-full min-w-[90px] font-semibold text-foreground"
                          />
                        </div>
                      </td>
                      <td className="p-2 pl-4 align-middle">
                        <Input
                          type="text"
                          readOnly
                          value={(Number(line.taxAmount) || 0).toFixed(2)}
                          className="h-10 w-full min-w-[90px] text-right bg-muted border border-input rounded-md text-sm font-medium"
                        />
                      </td>
                      <td className="p-2 align-middle">
                        <Input
                          type="text"
                          readOnly
                          value={(Number(line.amount) || 0).toFixed(2)}
                          className="h-10 w-full min-w-[90px] text-right bg-muted border border-input rounded-md text-sm font-medium"
                        />
                      </td>
                      <td className="p-2 align-middle">
                        <label
                          htmlFor={`file-${index}`}
                          className="flex h-10 w-full min-w-[160px] items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                        >
                          <Input
                            type="file"
                            className="hidden w-0 h-0"
                            id={`file-${index}`}
                            onChange={(e) => {
                              const f = e.target.files?.[0]
                              if (f) setLineFiles((prev) => ({ ...prev, [index]: f }))
                            }}
                          />
                          <span className="text-primary font-medium shrink-0">Choose File</span>
                          <span className="truncate text-muted-foreground text-xs">
                            {lineFiles[index] ? lineFiles[index].name : 'No file chosen'}
                          </span>
                        </label>
                      </td>
                      <td className="p-2 align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-destructive hover:bg-destructive/10 rounded-md border border-transparent hover:border-destructive/20"
                          onClick={() => removeLine(index)}
                          title="Remove row"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 border-t border-input pt-4">
              <div className="bg-muted/50 rounded-md border border-input p-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Sub Total</p>
                    <p className="text-lg font-semibold">{Number(totals.subTotal).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total VAT</p>
                    <p className="text-lg font-semibold">{Number(totals.totalVat).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total</p>
                    <p className="text-lg font-semibold">{Number(totals.total).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-end">
          <Button type="button" variant="outline" disabled={saving || loading} onClick={() => navigate('/self-services/expense-claims')}>
            Return
          </Button>
          <Button type="submit" disabled={saving || loading} className="ml-2">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}
